import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, MenuItem, Alert, Checkbox, List, ListItem, ListItemText, ListItemIcon, Divider } from '@mui/material';

// Simulated API call to fetch clients with outstanding balances
const fetchOutstandingClients = async () => [
  { id: 1, name: 'Acme Corp', email: 'acme@example.com', phone: '+3212345678', balance: 120.50 },
  { id: 2, name: 'Beta Ltd', email: 'beta@example.com', phone: '+3298765432', balance: 80.00 },
  { id: 3, name: 'Gamma Inc', email: 'gamma@example.com', phone: '+3212215665', balance: 50.00 },
  { id: 4, name: 'Delta Co', email: 'delta@example.com', phone: '+3212345678', balance: 30.00 },
  { id: 5, name: 'Epsilon LLC', email: 'epsilon@example.com', phone: '+3212345678', balance: 20.00 },
  { id: 6, name: 'Zeta AG', email: 'zeta@example.com', phone: '+3212345678', balance: 10.00 },
  { id: 7, name: 'Eta Inc', email: 'eta@example.com', phone: '+3212345678', balance: 5.00 },
  { id: 8, name: 'Theta Ltd', email: 'theta@example.com', phone: '+3212345678', balance: 0.00 },
  { id: 9, name: 'Iota Corp', email: 'iota@example.com', phone: '+3212345678', balance: 0.00 },
  { id: 10, name: 'Theta Ltd', email: 'theta@example.com', phone: '+3212345678', balance: 0.00 },
  { id: 11, name: 'Iota Corp', email: 'iota@example.com', phone: '+3212345678', balance: 0.00 },
  { id: 12, name: 'Kappa LLC', email: 'kappa@example.com', phone: '+3212345678', balance: 0.00 },
  // ...more clients
];

const MAX_TEXT_LENGTH = 300;

const announcementTypes = [
  { value: 'holiday', label: 'Holiday Announcement' },
  { value: 'outstanding', label: 'Outstanding Payment' },
  
  { value: 'tax', label: 'Tax Declaration' },
  { value: 'custom', label: 'Custom' },
];

const AnnouncementPage = () => {
  // Set "holiday" as the default type
  const [type, setType] = useState('holiday');
  const [templateText, setTemplateText] = useState('');
  const [customText, setCustomText] = useState('');
  const [textLength, setTextLength] = useState(0);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [outstandingClients, setOutstandingClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  // Simulate fetching templates from company settings
  useEffect(() => {
    const templates = {
      outstanding: 'Dear customer, you have outstanding payments. Please settle your dues at the earliest.',
      holiday: 'Our office will be closed for the upcoming holidays. We wish you a happy holiday season!',
      tax: 'Please be informed about the upcoming tax declaration deadline. Contact us for assistance.',
      custom: '',
    };
    setTemplateText(templates[type]);
    setCustomText(templates[type]);
    setTextLength(templates[type].length);
    // Fetch outstanding clients if type is 'outstanding'
    if (type === 'outstanding') {
      fetchOutstandingClients().then(setOutstandingClients);
      setSelectedClients([]); // Reset selection on type change
    }
  }, [type]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_TEXT_LENGTH) {
      setCustomText(value);
      setTextLength(value.length);
    }
  };

  const handleClientToggle = (id) => {
    setSelectedClients((prev) =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleSend = () => {
    setError('');
    setSuccess('');
    if (!customText.trim()) {
      setError('Announcement text cannot be empty.');
      return;
    }
    if (type === 'outstanding' && selectedClients.length === 0) {
      setError('Please select at least one client with outstanding balance.');
      return;
    }
    // Replace with real API call to send announcement
    setTimeout(() => {
      setSuccess(
        type === 'outstanding'
          ? `Announcement sent to ${selectedClients.length} client(s) via SMS and Email!`
          : 'Announcement sent successfully via SMS and Email!'
      );
      setCustomText('');
      setTextLength(0);
      setSelectedClients([]);
    }, 1000);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
        Announcements
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select a template or write your own message. Announcements will be sent via SMS and Email.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        <Box sx={{ flex: 1 }}>
          <TextField
            select
            label="Announcement Type"
            value={type}
            onChange={e => setType(e.target.value)}
            fullWidth
            sx={{ mb: 3 }}
          >
            {announcementTypes.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {type === 'outstanding' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Select clients with outstanding balance:
              </Typography>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  maxHeight: 240,
                  minWidth: 280,
                  background: '#fafbfc',
                  overflow: 'auto',
                  p: 1,
                }}
              >
                {/* Select All Checkbox */}
                <List dense alignItems="flex-start">
                  <ListItem disablePadding>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        indeterminate={
                          selectedClients.length > 0 &&
                          selectedClients.length < outstandingClients.length
                        }
                        checked={
                          outstandingClients.length > 0 &&
                          selectedClients.length === outstandingClients.length
                        }
                        onChange={(_, checked) => {
                          if (checked) {
                            setSelectedClients(outstandingClients.map(c => c.id));
                          } else {
                            setSelectedClients([]);
                          }
                        }}
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Select All"
                      sx={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  {outstandingClients.length === 0 && (
                    <ListItem>
                      <ListItemText primary="No clients with outstanding balance." />
                    </ListItem>
                  )}
                  {outstandingClients.map(client => (
                    <ListItem
                      key={client.id}
                      secondaryAction={
                        <Typography color="error" fontWeight={600}>
                          €{client.balance.toFixed(2)}
                        </Typography>
                      }
                      disablePadding
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Checkbox
                          edge="start"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => handleClientToggle(client.id)}
                          tabIndex={-1}
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={client.name}
                        secondary={
                          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {client.email} | {client.phone}
                          </span>
                        }
                        sx={{ pr: 2 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 2 }}>
          <TextField
            label="Announcement Text"
            value={customText}
            onChange={handleTextChange}
            multiline
            minRows={7}
            fullWidth
            helperText={`${textLength}/${MAX_TEXT_LENGTH} characters`}
            inputProps={{ maxLength: MAX_TEXT_LENGTH }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSend}>
              Send Announcement
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setCustomText(templateText);
                setTextLength(templateText.length);
              }}
            >
              Reset to Template
            </Button>
          </Box>
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      </Box>
    </Box>
  );
};

export default AnnouncementPage;